// hamburger-toggle-control.ts
import { HamburgerToggleComponent } from "appworks/components/hamburger/hamburger-toggle-component";
import { ButtonElement } from "appworks/graphics/elements/button-element";
import { Services } from "appworks/services/services";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { AutoplayService } from "slotworks/services/autoplay/autoplay-service";
import { AbstractControl } from "../../abstract-control";
import { stateMachine } from "appworks/state-machine/state-machine";
import { AgsRollUpState } from "ags/state-machine/states/ags-roll-up-state";

/**
 * The HamburgerToggleControl class controls the state of the buttons that are associated with the Hamburger UI menu.
 * Instantiating this class will instantiate the HamburgerToggleComponent, which in turn defines the specific funcionality
 * of the hamburger menu.
 *
 * @property {HamburgerToggleComponent} hamburgerToggle - Represents the HamburgerToggleComponent associated with the control.
 * @property {ButtonElement[]} hamburgerButtons - An array of ButtonElements, representing the buttons associated with the Hamburger Menu UI.
 * @property {ButtonElement} betBarReference - A reference to the bet bar button in the UI, used for linking the state of specific Hamburger Menu buttons.
 *
 * @property {ButtonElement} expanded - Represents the 'expanded' state button of the Hamburger Menu.
 * @property {ButtonElement} collapsed - Represents the 'collapsed' state button of the Hamburger Menu.
 * @property {ButtonElement[]} buttons - An array of ButtonElements that represent additional buttons associated with the Hamburger Menu.
 *
 * @constructor
 * The constructor takes three parameters: `expanded`, `collapsed`, and `buttons`, which are ButtonElements of the Hamburger Menu.
 * Upon construction, the `hamburgerButtons` array is filled with the provided buttons and a new `HamburgerToggleComponent` is instantiated.
 * A reference to the 'ui_bet_button' from the 'BetBar' layer is also stored for future state linkage.
 */
export class HamburgerToggleControl extends AbstractControl {
    protected hamburgerToggle: HamburgerToggleComponent;
    protected hamburgerButtons: ButtonElement[];
    protected inProgressFlags = [UIFlag.PROMPT, UIFlag.FREE_SPINS, UIFlag.BIG_WIN_DELAY, UIFlag.BIG_WIN, UIFlag.GAME_IN_PROGRESS];

    constructor(protected expanded: ButtonElement, protected collapsed: ButtonElement, protected buttons: ButtonElement[]) {
        super();
        this.hamburgerButtons = [];
        this.hamburgerButtons.push(expanded);
        this.hamburgerButtons.push(collapsed);
        buttons.forEach(button => {
            this.hamburgerButtons.push(button);
        });

        this.hamburgerToggle = new HamburgerToggleComponent(expanded, collapsed, buttons, this);
    }

    /**
     * Overrides the `setEnabled` method of the superclass to enable or disable the buttons associated with the Hamburger UI component,
     * with some exceptions for certain button names and states.
     *
     * @param {boolean} enabled - Boolean value representing whether the component and its buttons should be enabled or disabled.
     *
     * This method assumes that there will never be a need to keep the "paytable" and "settings" buttons active when the menu is disabled.
     *
     * @returns {void}
     */
    public override setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        this.hamburgerButtons.forEach((button) => {
            if(!button.name.includes("sound")) {
                button.setEnabled(enabled);
            }

            // Comparing with enabled since there are no circumstances I can think of where we want to disable the menu but keep paytable and settings active.
            // This will cover situations where we need to disable the button with flags that are not being evaluated in this control block.
            if((button.name.includes("paytable") || button.name.includes("settings")) && enabled) {
                // Check whether the bitmask indicates we are in autospin or spin state (false evaluation indicates we should disable)
                // We need to manually check these since we want different functionalities for these buttons in particular.
                const isMidRollup = (stateMachine.superState?.concrete ?? null) != null && stateMachine.superState.concrete instanceof AgsRollUpState && !stateMachine.superState.concrete.isRollupComplete();
                const isInProgress = this.inProgressFlags.some((flag) => uiFlags.has(flag)) || isMidRollup;
                const isIdleInBase = uiFlags.has(UIFlag.IDLE) && !uiFlags.has(UIFlag.FREE_SPINS);
                const shouldEnableButton = !Services.get(AutoplayService).isAutoPlaying() && (isIdleInBase || !isInProgress);
                button.setEnabled(shouldEnableButton);
            }
        });
    }
}